import React, { useEffect, useMemo, useState } from "react";
import { X, Save, Sparkles, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "react-hot-toast";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { Category } from "@/services/types/domain";
import { categoriesApi } from "@/services/api/categories";

interface Props {
    open: boolean;
    mode: "create" | "edit";
    category?: Category;
    parents: Category[];
    onClose: () => void;
    onSaved: () => void;
}

type FormData = {
    name: string;
    slug: string;
    description?: string;
    parent_id: number | null;
    sort_order: number | "";
    is_active: boolean;
    meta_title?: string;
    meta_description?: string;
    image_url?: string | null; // preview actual/remota
    image?: string | null;
    icon?: string | null;
};

const slugify = (s: string) =>
    s
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");

const isActive = (val: boolean | number | undefined) => val === true || val === 1;

const CategoryFormModal: React.FC<Props> = ({ open, mode, category, parents, onClose, onSaved }) => {
    const [form, setForm] = useState<FormData>({
        name: "",
        slug: "",
        description: "",
        parent_id: null,
        sort_order: 0,
        is_active: true,
        meta_title: "",
        meta_description: "",
        image_url: null,
        image: null,
        icon: null,
    });
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [file, setFile] = useState<File | null>(null);     // ⬅️ archivo seleccionado
    const [filePreview, setFilePreview] = useState<string>(""); // ⬅️ preview local
    const isEdit = mode === "edit";

    useEffect(() => {
        if (!open) return;
        // Limpia selección de archivo cada vez que se abre
        setFile(null);
        setFilePreview("");

        if (isEdit && category) {
            setForm({
                name: category.name,
                slug: category.slug,
                description: category.description || "",
                parent_id: category.parent_id ?? null,
                sort_order: typeof category.sort_order === "number" ? category.sort_order : 0,
                is_active: isActive(category.is_active),
                meta_title: category.meta_title || "",
                meta_description: category.meta_description || "",
                image_url: category.image_url || category.image || null, // preferimos image_url
                image: category.image || null,
                icon: category.icon || null,
            });
        } else {
            setForm({
                name: "",
                slug: "",
                description: "",
                parent_id: null,
                sort_order: 0,
                is_active: true,
                meta_title: "",
                meta_description: "",
                image_url: null,
                image: null,
                icon: null,
            });
        }
    }, [open, isEdit, category]);

    const parentOptions = useMemo(() => {
        const excludeId = isEdit ? category?.id : undefined;
        return parents
            .filter((p) => p.id !== excludeId)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    }, [parents, isEdit, category]);

    const onChange =
        (k: keyof FormData) =>
            (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                const val =
                    k === "sort_order"
                        ? (e.target.value === "" ? "" : Number(e.target.value))
                        : k === "is_active"
                            ? (e as any).target.checked
                            : e.target.value;
                setForm((f) => ({ ...f, [k]: val as any }));
            };

    const handleGenerateSlug = () => {
        if (!form.name.trim()) {
            toast.error("Ingresa un nombre para generar el slug");
            return;
        }
        setForm((f) => ({ ...f, slug: slugify(f.name) }));
    };

    // ⬇️ IMAGEN: selección y preview local
    const onSelectFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!/^image\//.test(f.type)) {
            toast.error("Selecciona una imagen válida");
            return;
        }
        setFile(f);
        const reader = new FileReader();
        reader.onload = () => setFilePreview(String(reader.result || ""));
        reader.readAsDataURL(f);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name.trim()) return toast.error("El nombre es requerido");
        if (!form.slug.trim()) return toast.error("El slug es requerido");

        setBusy(true);
        try {
            // 1) Guardar/actualizar metadatos de categoría
            const payload: Partial<Category> = {
                name: form.name.trim(),
                slug: form.slug.trim(),
                description: form.description?.trim() || undefined,
                parent_id: form.parent_id,
                sort_order: form.sort_order === "" ? 0 : Number(form.sort_order),
                is_active: form.is_active ? 1 : 0, // backend acepta 1/0
                meta_title: form.meta_title?.trim() || undefined,
                meta_description: form.meta_description?.trim() || undefined,
            };

            let categoryId = category?.id ?? null;

            if (isEdit && category?.id) {
                await (categoriesApi as any).updateCategory(Number(category.id), payload);
            } else {
                const created: Category = await (categoriesApi as any).createCategory(payload);
                categoryId = created?.id ?? null;
            }

            // 2) Si hay archivo seleccionado y tenemos id => subir imagen
            console.log("file", file, "categoryId", categoryId);
            if (file && categoryId) {
                setUploading(true);
                try {
                    const { image_url } = await (categoriesApi as any).uploadCategoryImage(Number(categoryId), file);
                    // Actualizamos preview local por si el modal permanece abierto
                    setForm((f) => ({ ...f, image_url }));
                    toast.success("Imagen subida correctamente");
                } catch (uploadErr: any) {
                    const msg = uploadErr?.response?.data?.message || uploadErr?.message || "No se pudo subir la imagen";
                    toast.error(msg);
                } finally {
                    setUploading(false);
                }
            }

            toast.success(isEdit ? "Categoría actualizada" : "Categoría creada");
            onSaved();
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || "No se pudo guardar la categoría";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />

            <div className="relative z-[61] w-full max-w-2xl rounded-2xl border bg-white shadow-xl">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-primary-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                            {isEdit ? "Editar categoría" : "Nueva categoría"}
                        </h3>
                    </div>
                    <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-50">
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                        label="Nombre"
                        placeholder="Ej. Electrónicos"
                        value={form.name}
                        onChange={onChange("name")}
                        required
                        fullWidth
                    />

                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Slug</label>
                        <div className="flex gap-2">
                            <input
                                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                placeholder="electronicos"
                                value={form.slug}
                                onChange={onChange("slug")}
                                required
                            />
                            <Button type="button" variant="outline" onClick={handleGenerateSlug}>
                                Generar
                            </Button>
                        </div>
                    </div>

                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Descripción</label>
                        <textarea
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            rows={3}
                            placeholder="Describe la categoría…"
                            value={form.description}
                            onChange={onChange("description")}
                        />
                    </div>

                    {/* Padre */}
                    <div className="sm:col-span-1">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Categoría padre</label>
                        <select
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={form.parent_id === null ? "" : String(form.parent_id)}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, parent_id: e.target.value === "" ? null : Number(e.target.value) }))
                            }
                        >
                            <option value="">Sin padre (raíz)</option>
                            {parentOptions.map((p) => (
                                <option key={p.id ?? `p-${p.slug}-${p.name}`} value={p.id ?? ""}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Orden */}
                    <Input
                        label="Orden"
                        placeholder="0"
                        type="number"
                        value={form.sort_order as any}
                        onChange={onChange("sort_order")}
                    />

                    {/* Meta */}
                    <Input
                        label="Meta título"
                        placeholder="Para SEO"
                        value={form.meta_title}
                        onChange={onChange("meta_title")}
                        fullWidth
                    />
                    <Input
                        label="Meta descripción"
                        placeholder="Para SEO"
                        value={form.meta_description}
                        onChange={onChange("meta_description")}
                        fullWidth
                    />

                    {/* ⬇️ IMAGEN */}
                    <div className="sm:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-gray-700">Imagen de la categoría</label>
                        <div className="flex items-start gap-4">
                            <div className="w-28 h-20 rounded-md overflow-hidden border bg-gray-50 shrink-0">
                                {filePreview || form.image_url ? (
                                    <img
                                        src={filePreview || form.image_url!}
                                        alt="Preview"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full grid place-items-center text-gray-300">
                                        <ImageIcon className="w-6 h-6" />
                                    </div>
                                )}
                            </div>
                            <div className="flex-1">
                                <label className="inline-flex items-center px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-gray-50">
                                    <Upload className="w-4 h-4 mr-2" />
                                    <span>Seleccionar imagen</span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={onSelectFile}
                                    />
                                </label>
                                <p className="mt-1 text-xs text-gray-500">
                                    JPG/PNG recomendados. El archivo se subirá al guardar.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Estado */}
                    <div className="sm:col-span-2 flex items-center gap-2 mt-1">
                        <input
                            id="active"
                            type="checkbox"
                            checked={form.is_active}
                            onChange={onChange("is_active")}
                            className="h-4 w-4"
                        />
                        <label htmlFor="active" className="text-sm text-gray-700">
                            Activa
                        </label>
                    </div>

                    <div className="sm:col-span-2 flex items-center justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={busy || uploading}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={busy || uploading} leftIcon={<Save className="w-4 h-4" />}>
                            {busy ? "Guardando…" : isEdit ? (uploading ? "Subiendo imagen…" : "Guardar cambios") : (uploading ? "Subiendo imagen…" : "Crear categoría")}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CategoryFormModal;
